import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ApStatusBadge, AgingBadge } from "@/components/ap/ApStatusBadge";
import { PaymentDialog } from "@/components/ap/PaymentDialog";
import { SupplierDocumentsCard } from "@/components/supplier-documents/SupplierDocumentsCard";
import { money } from "@/lib/constants";
import {
  ArrowLeft, RefreshCw, Wallet, PackageCheck, History, X, Calendar, Save,
} from "lucide-react";

function Row({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</dt>
      <dd className={`text-sm text-slate-800 ${mono ? "mono" : ""}`}>{value || "—"}</dd>
    </div>
  );
}

const METHOD_LABEL = {
  efectivo: "Efectivo",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
  otro: "Otro",
};

export default function CuentaPorPagarDetalle() {
  const { apId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [ap, setAp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  const [dueEditing, setDueEditing] = useState(false);
  const [dueDraft, setDueDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");

  const canPay = hasPermission("accounts_payable:pay");
  const canCancel = hasPermission("accounts_payable:cancel");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounts-payable/${apId}`);
      setAp(data);
      setDueDraft(data.due_date || "");
      setNotesDraft(data.notes || "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [apId]);

  useEffect(() => { load(); }, [load]);

  async function saveMeta() {
    setBusy(true);
    try {
      const { data } = await api.patch(`/accounts-payable/${apId}`, {
        due_date: dueDraft || null,
        notes: notesDraft || null,
      });
      setAp((prev) => ({ ...prev, ...data }));
      setDueEditing(false);
      toast.success("Datos actualizados");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancelAp() {
    if (!cancelReason.trim()) { toast.error("Motivo requerido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/accounts-payable/${apId}/cancel`, { reason: cancelReason.trim() });
      setAp((prev) => ({ ...prev, ...data }));
      setCancelOpen(false);
      setCancelReason("");
      toast.success("CxP cancelada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !ap) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const canRegisterPayment = canPay && !["paid", "cancelled"].includes(ap.status);
  const canCancelNow = canCancel && ap.status !== "cancelled" && Number(ap.total_paid_amount || 0) === 0;

  return (
    <div className="space-y-6" data-testid="page-cuenta-detalle">
      <Button variant="ghost" size="sm" onClick={() => navigate("/cuentas-por-pagar")} data-testid="ap-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a cuentas por pagar
      </Button>

      <PageHeader
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <Wallet className="h-6 w-6 text-tenant" />
            <span className="mono text-tenant">{ap.po_folio}</span>
            <ApStatusBadge status={ap.status} />
            <AgingBadge bucket={ap.aging_bucket} days={ap.age_days} />
          </span>
        }
        description={<span>{ap.supplier_name} · {ap.branch_name}</span>}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} data-testid="ap-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
            </Button>
            {canRegisterPayment && (
              <Button
                size="sm"
                className="btn-tenant"
                onClick={() => setPaymentOpen(true)}
                data-testid="ap-register-payment"
              >
                <Wallet className="h-4 w-4 mr-1" /> Registrar pago
              </Button>
            )}
            {canCancelNow && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => setCancelOpen(true)}
                data-testid="ap-cancel"
              >
                <X className="h-4 w-4 mr-1" /> Cancelar CxP
              </Button>
            )}
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4">
            <div className="text-xs uppercase text-muted-foreground">Total recibido</div>
            <div className="text-xl font-semibold mono mt-1">{money(ap.total_received_amount)}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4">
            <div className="text-xs uppercase text-muted-foreground">Total pagado</div>
            <div className="text-xl font-semibold mono mt-1">{money(ap.total_paid_amount)}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="ap-summary-balance">
          <CardContent className="pt-4">
            <div className="text-xs uppercase text-muted-foreground">Saldo pendiente</div>
            <div className="text-2xl font-semibold text-tenant mono mt-1">{money(ap.balance_due)}</div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4">
            <div className="text-xs uppercase text-muted-foreground">Estatus</div>
            <div className="mt-1"><ApStatusBadge status={ap.status} /></div>
            <div className="text-xs text-muted-foreground mt-1">
              Creada {new Date(ap.created_at).toLocaleDateString("es-MX")}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meta (due date, notes) */}
      <Card className="border-border">
        <CardHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Vencimiento y notas
            </CardTitle>
            {canPay && ap.status !== "cancelled" && (
              dueEditing ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setDueEditing(false); setDueDraft(ap.due_date || ""); setNotesDraft(ap.notes || ""); }}>Cancelar</Button>
                  <Button size="sm" className="btn-tenant" onClick={saveMeta} disabled={busy} data-testid="ap-meta-save">
                    <Save className="h-4 w-4 mr-1" /> Guardar
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setDueEditing(true)} data-testid="ap-meta-edit">Editar</Button>
              )
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {dueEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha de vencimiento (opcional)</Label>
                <Input
                  type="date"
                  value={dueDraft}
                  onChange={(e) => setDueDraft(e.target.value)}
                  data-testid="ap-meta-due"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas internas</Label>
                <Textarea rows={2} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} data-testid="ap-meta-notes" />
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Row label="Vencimiento" value={ap.due_date || "Sin fecha"} />
              <Row label="Notas" value={ap.notes} />
              {ap.cancel_reason && <Row label="Motivo de cancelación" value={ap.cancel_reason} />}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Payments history */}
      <Card className="border-border" data-testid="ap-payments-card">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" /> Historial de pagos ({ap.payments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          {(ap.payments || []).length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              Aún no hay pagos registrados.
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Registrado por</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ap.payments.map((p) => (
                  <TableRow key={p.id} data-testid={`ap-payment-${p.id}`}>
                    <TableCell className="text-sm">{p.payment_date}</TableCell>
                    <TableCell className="text-sm">
                      {METHOD_LABEL[p.payment_method] || p.payment_method}
                      {p.method_note && <span className="text-xs text-muted-foreground"> · {p.method_note}</span>}
                    </TableCell>
                    <TableCell className="text-xs mono">{p.reference || "—"}</TableCell>
                    <TableCell className="text-xs">{p.created_by_name || "—"}</TableCell>
                    <TableCell className="text-right mono font-medium">{money(p.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Linked receptions context */}
      <Card className="border-border">
        <CardHeader className="border-b border-border pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <PackageCheck className="h-4 w-4" /> Recepciones ligadas ({ap.receptions?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-2">
          {(ap.receptions || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">Sin recepciones asociadas.</div>
          ) : (
            (ap.receptions || []).map((r) => (
              <div key={r.id} className="border border-border rounded-md p-2 text-sm bg-slate-50/50">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{new Date(r.received_at).toLocaleString("es-MX")}</span>
                  <span>{r.lines?.length || 0} línea{r.lines?.length === 1 ? "" : "s"}</span>
                </div>
                {r.notes && <div className="text-xs italic mt-1">“{r.notes}”</div>}
              </div>
            ))
          )}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => navigate(`/compras/${ap.purchase_order_id}`)}
            data-testid="ap-open-po"
          >
            Ver orden de compra completa →
          </Button>
        </CardContent>
      </Card>

      <SupplierDocumentsCard entityType="ap" entityId={ap.id} testIdPrefix="ap-docs" />

      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        ap={ap}
        onSuccess={(updated) => {
          if (updated) setAp((prev) => ({ ...prev, ...updated }));
          load();
        }}
      />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="ap-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar cuenta por pagar</DialogTitle>
            <DialogDescription>
              La CxP quedará en estatus <b>cancelada</b>. Solo se puede cancelar si <b>no hay pagos registrados</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Motivo *</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="ap-cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Cerrar</Button>
            <Button
              className="btn-tenant"
              disabled={busy || !cancelReason.trim()}
              onClick={cancelAp}
              data-testid="ap-cancel-submit"
            >
              Cancelar CxP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
